import { Button } from "@/components/ui/button";
import { FolderOpen, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { cn } from "@/lib/utils";
import { Database } from "@/integrations/supabase/types";

type ProcessingStatus = Database["public"]["Enums"]["processing_status"];

export type EmptyStateType = "no-records" | "no-filter-results" | "no-search-results" | "combined-empty";

interface BaseEmptyStateProps {
  className?: string;
  onUpload?: () => void;
}

interface NoRecordsEmptyStateProps extends BaseEmptyStateProps {
  type: "no-records";
}

interface NoFilterResultsEmptyStateProps extends BaseEmptyStateProps {
  type: "no-filter-results";
  statusFilter: ProcessingStatus;
  onShowAll: () => void;
}

interface NoSearchResultsEmptyStateProps extends BaseEmptyStateProps {
  type: "no-search-results";
  searchTerm: string;
  onClearSearch: () => void;
  onShowAll: () => void;
}

interface CombinedEmptyStateProps extends BaseEmptyStateProps {
  type: "combined-empty";
  searchTerm: string;
  statusFilter: ProcessingStatus;
  onClearSearch: () => void;
  onResetFilters: () => void;
  onShowAll: () => void;
}

type EnhancedEmptyStateProps =
  | NoRecordsEmptyStateProps
  | NoFilterResultsEmptyStateProps
  | NoSearchResultsEmptyStateProps
  | CombinedEmptyStateProps;

// Status label mapping
const getStatusLabel = (status: ProcessingStatus, t: TFunction): string => {
  return t(`records.status.${status}`, { defaultValue: status });
};

export const EnhancedEmptyState: React.FC<EnhancedEmptyStateProps> = (props) => {
  const { className, type } = props;
  const { t } = useTranslation();

  const renderContent = () => {
    switch (type) {
      case "no-records":
        return (
          <>
            <div className="mx-auto h-16 w-16 bg-muted rounded-full flex items-center justify-center mb-6">
              <FolderOpen size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('dashboard.empty_states.no_records.title')}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {t('dashboard.empty_states.no_records.description')}
            </p>
            {props.onUpload && (
              <Button onClick={props.onUpload} size="lg">
                {t('dashboard.empty_states.no_records.action')}
              </Button>
            )}
          </>
        );

      case "no-filter-results":
        return (
          <>
            <div className="mx-auto h-16 w-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
              <Search size={32} className="text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('dashboard.empty_states.no_filter_results.title', { status: getStatusLabel(props.statusFilter, t) })}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {t('dashboard.empty_states.no_filter_results.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={props.onShowAll} variant="default">
                {t('dashboard.empty_states.no_filter_results.action')}
              </Button>
            </div>
          </>
        );

      case "no-search-results":
        return (
          <>
            <div className="mx-auto h-16 w-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
              <Search size={32} className="text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('dashboard.empty_states.no_search_results.title', { search: props.searchTerm })}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {t('dashboard.empty_states.no_search_results.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={props.onClearSearch} variant="outline">
                {t('dashboard.empty_states.no_search_results.action_clear')}
              </Button>
              <Button onClick={props.onShowAll} variant="default">
                {t('dashboard.empty_states.no_search_results.action_all')}
              </Button>
            </div>
          </>
        );

      case "combined-empty":
        return (
          <>
            <div className="mx-auto h-16 w-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
              <Search size={32} className="text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('dashboard.empty_states.combined_empty.title', { status: getStatusLabel(props.statusFilter, t), search: props.searchTerm })}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {t('dashboard.empty_states.combined_empty.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={props.onClearSearch} variant="outline">
                {t('dashboard.empty_states.combined_empty.action_clear')}
              </Button>
              <Button onClick={props.onResetFilters} variant="outline">
                {t('dashboard.empty_states.combined_empty.action_reset')}
              </Button>
              <Button onClick={props.onShowAll} variant="default">
                {t('dashboard.empty_states.combined_empty.action_all')}
              </Button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className={cn(
      "text-center py-12 px-6 border border-border rounded-lg bg-card",
      className
    )}>
      {renderContent()}
    </div>
  );
};

// Helper function to determine empty state type
// eslint-disable-next-line react-refresh/only-export-components
export const getEmptyStateType = (
  hasRecords: boolean,
  searchTerm: string,
  statusFilter: ProcessingStatus | "all"
): EmptyStateType => {
  const hasSearch = searchTerm.trim().length > 0;
  const hasStatusFilter = statusFilter !== "all";

  // If we have search and status filter both applied
  if (hasSearch && hasStatusFilter) {
    return "combined-empty";
  }

  // If only search is applied
  if (hasSearch && !hasStatusFilter) {
    return "no-search-results";
  }

  // If only status filter is applied
  if (!hasSearch && hasStatusFilter) {
    return "no-filter-results";
  }

  // No filters applied - show general empty state
  if (!hasSearch && !hasStatusFilter) {
    return "no-records";
  }

  // Fallback
  return "no-records";
};